<template>
  <div class="import-modal-wrap" tabindex="-1" @click.self="close">
    <div class="import-modal">
      <!-- Header -->
      <div class="import-modal-header">
        <div class="import-icon">
          <i class="fas fa-file-import"></i>
        </div>
        <div class="import-header-text">
          <h2 class="import-title">Auftrag importieren</h2>
          <div class="import-subtitle">
            <span v-if="step === 'pick'">JDF, XML oder DOCX Datei auswählen</span>
            <span v-else-if="preview">{{ preview.action === 'update' ? 'Änderungen prüfen und übernehmen' : 'Neuen Auftrag prüfen und erstellen' }}</span>
          </div>
        </div>
        <button type="button" class="close-btn" @click="close" :disabled="busy" aria-label="Schließen">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="import-modal-body">

        <!-- ───── Step 1: file picker ───── -->
        <template v-if="step === 'pick'">
          <label class="dropzone" :class="{ 'has-file': selectedFile, 'is-busy': busy }">
            <input
                type="file"
                class="dropzone-input"
                @change="handleFileChange"
                accept=".jdf,.xml,.docx"
                :disabled="busy"
            >
            <div v-if="!selectedFile" class="dropzone-empty">
              <div class="dropzone-icon"><i class="fas fa-cloud-upload-alt"></i></div>
              <div class="dropzone-primary">Datei auswählen</div>
              <div class="dropzone-hint">
                Erlaubte Formate: <strong>.jdf</strong>, <strong>.xml</strong>, <strong>.docx</strong>
              </div>
              <div class="dropzone-sub">JDF / Prinance JDFAdapter</div>
            </div>
            <div v-else class="dropzone-filled">
              <div class="file-pill">
                <i class="fas fa-file-alt file-pill-icon"></i>
                <div class="file-pill-meta">
                  <div class="file-pill-name">{{ selectedFile.name }}</div>
                  <div class="file-pill-size">{{ formatFileSize(selectedFile.size) }}</div>
                </div>
              </div>
              <div class="dropzone-change-hint"><i class="fas fa-sync-alt"></i> Klicken um andere Datei zu wählen</div>
            </div>
          </label>

          <div v-if="busy" class="busy-state">
            <div class="spinner-border text-primary" role="status"></div>
            <p>Datei wird analysiert…</p>
          </div>

          <div v-if="errorMessage" class="error-banner">
            <i class="fas fa-exclamation-circle"></i>
            <span>{{ errorMessage }}</span>
          </div>
        </template>

        <!-- ───── Step 2: preview / diff ───── -->
        <template v-else-if="step === 'preview' && preview">
          <!-- Status pill -->
          <div class="status-banner" :class="preview.action === 'update' ? 'banner-update' : 'banner-create'">
            <i :class="preview.action === 'update' ? 'fas fa-pen-to-square' : 'fas fa-circle-plus'"></i>
            <div>
              <strong>
                {{ preview.action === 'update'
                    ? `Auftrag ${preview.parsed.order.order_number} existiert bereits`
                    : `Neuer Auftrag ${preview.parsed.order.order_number}` }}
              </strong>
              <div class="status-banner-sub">
                {{ preview.action === 'update' ? 'würde aktualisiert werden' : 'würde erstellt werden' }}
                <span v-if="preview.file_type"> · Format: <code>{{ preview.file_type }}</code></span>
              </div>
            </div>
          </div>

          <!-- Customer block -->
          <div class="info-card">
            <div class="info-card-header">
              <i class="fas fa-building"></i>
              <h3>Kunde</h3>
            </div>
            <div class="info-card-body">
              <div v-for="(value, key) in customerFields" :key="`c-${key}`">
                <div v-if="value" class="info-row">
                  <span class="info-label">{{ customerLabels[key] || key }}</span>
                  <span class="info-value">{{ value }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Order block -->
          <div class="info-card">
            <div class="info-card-header">
              <i class="fas fa-file-invoice"></i>
              <h3>Auftrag</h3>
            </div>
            <div class="info-card-body">
              <div v-for="(value, key) in orderFields" :key="`o-${key}`">
                <div v-if="value !== null && value !== '' && value !== undefined" class="info-row">
                  <span class="info-label">{{ orderLabels[key] || key }}</span>
                  <span class="info-value">{{ value }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Komplett-Extern (#11) — set at import time, red ring in OrderFlags -->
          <div class="info-card extern-card" :class="{ 'extern-on': extern }">
            <div class="info-card-body">
              <label class="extern-wrap">
                <input v-model="extern" type="checkbox">
                <span class="extern-dot"></span>
                <span>
                  <strong>Komplett extern (X)</strong>
                  <span class="extern-sub">
                    Auftrag wird vollständig ausgelagert — Maschine wird auf
                    „Extern (X)" gesetzt, der Auftrag erscheint mit rotem Ring.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <!-- Diff (only when updating) -->
          <div v-if="preview.action === 'update' && preview.diff && preview.diff.length > 0" class="info-card diff-card">
            <div class="info-card-header">
              <i class="fas fa-exchange-alt"></i>
              <h3>Änderungen ({{ preview.diff.length }})</h3>
            </div>
            <div class="info-card-body">
              <div v-for="(d, idx) in preview.diff" :key="`d-${idx}`" class="diff-row">
                <div class="diff-field">{{ orderLabels[d.field] || d.field }}</div>
                <div class="diff-values">
                  <span class="diff-old"><del>{{ formatVal(d.current) }}</del></span>
                  <i class="fas fa-arrow-right diff-arrow"></i>
                  <span class="diff-new">{{ formatVal(d.new) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="preview.action === 'update' && (!preview.diff || preview.diff.length === 0)" class="info-banner">
            <i class="fas fa-info-circle"></i>
            Keine Änderungen im Vergleich zum bestehenden Auftrag.
          </div>

          <div v-if="preview.warnings && preview.warnings.length" class="warning-banner">
            <i class="fas fa-triangle-exclamation"></i>
            <div>
              <strong>Hinweise:</strong>
              <ul>
                <li v-for="(w, i) in preview.warnings" :key="`w-${i}`">{{ w }}</li>
              </ul>
            </div>
          </div>

          <div v-if="busy" class="busy-state">
            <div class="spinner-border text-primary" role="status"></div>
            <p>{{ commitInProgressLabel }}</p>
          </div>

          <div v-if="errorMessage" class="error-banner">
            <i class="fas fa-exclamation-circle"></i>
            <span>{{ errorMessage }}</span>
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div class="import-modal-footer">
        <template v-if="step === 'pick'">
          <button type="button" class="btn-action btn-cancel" @click="close" :disabled="busy">
            <i class="fas fa-times"></i><span>Abbrechen</span>
          </button>
          <button
              type="button"
              class="btn-action btn-primary"
              @click="runPreview"
              :disabled="!selectedFile || busy"
          >
            <i class="fas fa-search"></i><span>Vorschau</span>
          </button>
        </template>

        <template v-else-if="step === 'preview'">
          <button type="button" class="btn-action btn-link" @click="resetToPick" :disabled="busy">
            <i class="fas fa-arrow-left"></i><span>Andere Datei</span>
          </button>
          <div class="footer-spacer"></div>
          <button type="button" class="btn-action btn-cancel" @click="close" :disabled="busy">
            <i class="fas fa-times"></i><span>Abbrechen</span>
          </button>
          <button
              type="button"
              class="btn-action"
              :class="preview && preview.action === 'update' ? 'btn-update' : 'btn-create'"
              @click="commitImport"
              :disabled="busy || (preview && preview.action === 'update' && (!preview.diff || preview.diff.length === 0))"
          >
            <i class="fas" :class="preview && preview.action === 'update' ? 'fa-pen' : 'fa-check'"></i>
            <span>{{ preview && preview.action === 'update' ? 'Aktualisieren' : 'Erstellen' }}</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';


export default {
  name: 'JdfUploadComponent',

  data() {
    return {
      step: 'pick', // 'pick' | 'preview'
      selectedFile: null,
      busy: false,
      preview: null,
      extern: false, // #11 Komplett-Extern; pre-ticked from preview.suggest_extern
      errorMessage: '',
      customerLabels: {
        customer_number: 'Kundennr.',
        name: 'Name',
        name2: 'Zusatz',
        street: 'Straße',
        postal_code: 'PLZ',
        city: 'Ort',
        country: 'Land',
        phone: 'Telefon',
        email: 'E-Mail'
      },
      orderLabels: {
        order_number: 'Auftragsnr.',
        description: 'Beschreibung',
        delivery: 'Lieferung',
        delivery_year: 'Lieferjahr',
        delivery_month: 'Liefermonat',
        delivery_day: 'Liefertag',
        delivery_hour: 'Lieferuhrzeit',
        print: 'Druck',
        print_year: 'Druckjahr',
        print_month: 'Druckmonat',
        print_day: 'Drucktag',
        print_hour: 'Druckuhrzeit',
        print_run: 'Auflage',
        print_type: 'Druckverfahren',
        page_count: 'Umfang',
        format: 'Format',
        final_length: 'Endformat Höhe',
        final_width: 'Endformat Breite',
        color: 'Farben',
        paper_type: 'Papier',
        paper_weight: 'Grammatur',
        notes: 'Anmerkungen',
        priority: 'Priorität',
        maschine_name: 'Maschine',
        farbigkeit: 'Farbigkeit',
        raster_output: 'Rasterausgabe',
        customer_order_number: 'Bestell-Nr. Kunde',
        shipping_address: 'Lieferadresse',
        sachbearbeiter: 'Sachbearbeiter',
        is_schoen_wider: 'Schön- und Widerdruck (S+W)',
        is_umschlagen: 'Umschlagen (US)',
        is_umstuelpen: 'Umstülpen (UP)',
        is_wendeeinrichtung: 'mit Wendeeinrichtung (WE)'
      }
    };
  },

  computed: {
    customerFields() {
      const c = this.preview?.parsed?.customer || {};
      return {
        customer_number: c.customer_number,
        name: c.name,
        name2: c.name2,
        street: c.street,
        postal_code: c.postal_code,
        city: c.city,
        country: c.country,
        phone: c.phone,
        email: c.email
      };
    },
    orderFields() {
      const o = this.preview?.parsed?.order || {};
      return {
        order_number: o.order_number,
        description: o.description,
        delivery: this.formatDateParts(o.delivery_year, o.delivery_month, o.delivery_day, o.delivery_hour),
        print: this.formatDateParts(o.print_year, o.print_month, o.print_day, o.print_hour),
        print_run: o.print_run,
        page_count: o.page_count,
        format: o.format,
        color: o.color,
        paper_type: o.paper_type,
        paper_weight: o.paper_weight,
        maschine_name: o.maschine_name,
        priority: o.priority
      };
    },
    commitInProgressLabel() {
      return this.preview?.action === 'update' ? 'Wird aktualisiert…' : 'Wird erstellt…';
    },
    authHeaders() {
      const token = localStorage.getItem('token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
  },

  methods: {
    handleFileChange(event) {
      this.selectedFile = event.target.files[0] || null;
      this.errorMessage = '';
    },

    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    },

    resetToPick() {
      this.step = 'pick';
      this.preview = null;
      this.selectedFile = null;
      this.errorMessage = '';
    },

    formatVal(v) {
      if (v === null || v === undefined || v === '') return '—';
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    },

    formatDateParts(y, m, d, h) {
      if (!y || !m || !d) return null;
      const pad = n => String(n).padStart(2, '0');
      const base = `${pad(d)}.${pad(m)}.${y}`;
      return h ? `${base} ${pad(h)}:00` : base;
    },

    async runPreview() {
      if (!this.selectedFile) return;
      this.busy = true;
      this.errorMessage = '';
      this.preview = null;
      this.extern = false;

      const formData = new FormData();
      formData.append('file', this.selectedFile);

      try {
        const { data } = await axios.post('/api/v1/orders/import_preview', formData, {
          headers: { 'Content-Type': 'multipart/form-data', ...this.authHeaders }
        });
        this.preview = data;
        // The file already points at the Extern lane → pre-tick, operator can undo.
        this.extern = !!data.suggest_extern;
        this.step = 'preview';
      } catch (error) {
        this.errorMessage = error.response?.data?.error
            || error.response?.data?.message
            || 'Vorschau fehlgeschlagen';
      } finally {
        this.busy = false;
      }
    },

    async commitImport() {
      if (!this.selectedFile || !this.preview) return;
      this.busy = true;
      this.errorMessage = '';

      const formData = new FormData();
      formData.append('file', this.selectedFile);
      if (this.preview.action === 'update') {
        formData.append('update', '1');
      }
      if (this.extern) {
        formData.append('extern', '1');
      }

      try {
        const { data } = await axios.post('/api/v1/orders/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data', ...this.authHeaders }
        });

        const verb = data.action === 'updated' ? 'aktualisiert' : 'erstellt';
        const msg = data.message || `Auftrag erfolgreich ${verb}`;
        if (this.$toast?.success) {
          this.$toast.success(msg);
        } else {
          // eslint-disable-next-line no-alert
          alert(msg);
        }

        // Tell parent we changed something so it reloads the list
        this.$emit('close', { success: true, action: data.action, order_id: data.order_id });
      } catch (error) {
        this.errorMessage = error.response?.data?.error
            || error.response?.data?.message
            || 'Import fehlgeschlagen';
      } finally {
        this.busy = false;
      }
    },

    close() {
      if (!this.busy) {
        this.$emit('close', false);
      }
    }
  }
};
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════
   Modal shell — matches ShowView/EditView aesthetics
   ════════════════════════════════════════════════════════════ */
.import-modal-wrap {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
  backdrop-filter: blur(2px);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 3rem 1rem;
  overflow-y: auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.import-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modal-pop 0.18s ease-out;
}

@keyframes modal-pop {
  from { opacity: 0; transform: translateY(-8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

/* ── Header ── */
.import-modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
}

.import-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.import-header-text { flex: 1; min-width: 0; }

.import-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.02em;
}

.import-subtitle {
  color: #6b7280;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.close-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}
.close-btn:hover:not(:disabled) { background: #f3f4f6; color: #111827; }
.close-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Body ── */
.import-modal-body {
  padding: 1.5rem 1.75rem;
  background: #f8f9fa;
  max-height: 65vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ── Dropzone ── */
.dropzone {
  position: relative;
  display: block;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 2.5rem 1.5rem;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.dropzone:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}
.dropzone.has-file {
  border-style: solid;
  border-color: #10b981;
  background: #f0fdf4;
  padding: 1.5rem;
}
.dropzone.is-busy { pointer-events: none; opacity: 0.7; }

.dropzone-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.dropzone-icon {
  font-size: 2.5rem;
  color: #9ca3af;
  margin-bottom: 0.75rem;
}
.dropzone:hover .dropzone-icon { color: #3b82f6; }

.dropzone-primary {
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

.dropzone-hint {
  color: #6b7280;
  font-size: 0.875rem;
}
.dropzone-hint strong { color: #111827; font-weight: 600; }

.dropzone-sub {
  color: #9ca3af;
  font-size: 0.78rem;
  margin-top: 0.35rem;
}

.dropzone-filled { display: flex; flex-direction: column; align-items: center; gap: 0.75rem; }

.file-pill {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1.25rem;
  background: white;
  border: 1px solid #d1fae5;
  border-radius: 10px;
  max-width: 100%;
}

.file-pill-icon {
  font-size: 1.5rem;
  color: #10b981;
  flex-shrink: 0;
}

.file-pill-meta { min-width: 0; text-align: left; }

.file-pill-name {
  font-weight: 600;
  color: #111827;
  font-size: 0.9rem;
  word-break: break-all;
}

.file-pill-size {
  color: #6b7280;
  font-size: 0.78rem;
  margin-top: 0.15rem;
}

.dropzone-change-hint {
  color: #6b7280;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

/* ── Status banners ── */
.status-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  font-size: 0.9rem;
}
.status-banner i { font-size: 1.25rem; margin-top: 2px; flex-shrink: 0; }
.status-banner-sub {
  font-size: 0.78rem;
  margin-top: 0.2rem;
  opacity: 0.8;
}
.status-banner code {
  background: rgba(0, 0, 0, 0.08);
  padding: 0.05rem 0.4rem;
  border-radius: 4px;
  font-size: 0.78rem;
}
.banner-create { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
.banner-update { background: #fef3c7; color: #78350f; border: 1px solid #fcd34d; }

.info-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #4b5563;
  font-size: 0.875rem;
}
.info-banner i { color: #6b7280; }

.warning-banner {
  display: flex;
  gap: 0.75rem;
  padding: 0.875rem 1.125rem;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  color: #78350f;
  font-size: 0.875rem;
}
.warning-banner i { font-size: 1.1rem; margin-top: 2px; }
.warning-banner ul { margin: 0.35rem 0 0; padding-left: 1.25rem; }

.error-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-size: 0.875rem;
}
.error-banner i { font-size: 1rem; }

.busy-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;
  color: #6b7280;
  font-size: 0.875rem;
}
.busy-state p { margin: 0; }

/* ── Info card (matches ShowView) ── */
.info-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.info-card-header i { color: #6b7280; font-size: 1rem; }
.info-card-header h3 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

.info-card-body { padding: 0.5rem 1.25rem; }

.info-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 1rem;
  padding: 0.625rem 0;
  border-bottom: 1px solid #f3f4f6;
  align-items: baseline;
}
.info-card-body > div:last-child .info-row { border-bottom: none; }

/* Komplett-Extern (#11) — mirrors the red ring OrderFlags draws on the dot */
.extern-card { border-color: #e5e7eb; }
.extern-card.extern-on {
  border-color: #dc2626;
  background: #fef2f2;
}
.extern-wrap {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.5rem 0;
  cursor: pointer;
}
.extern-wrap input { margin-top: 0.2rem; }
.extern-dot {
  width: 14px;
  height: 14px;
  margin-top: 0.15rem;
  border-radius: 50%;
  background: #1d6dff;
  box-shadow: 0 0 0 3px #fff, 0 0 0 5px #dc2626;
  flex: 0 0 auto;
}
.extern-sub {
  display: block;
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.15rem;
}

.info-label {
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
}
.info-value {
  color: #111827;
  font-size: 0.875rem;
  word-break: break-word;
}

/* ── Diff rows ── */
.diff-card .info-card-header {
  background: #fef3c7;
  border-bottom-color: #fcd34d;
}
.diff-card .info-card-header i,
.diff-card .info-card-header h3 { color: #78350f; }

.diff-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 1rem;
  padding: 0.625rem 0;
  border-bottom: 1px solid #f3f4f6;
  align-items: baseline;
}
.diff-row:last-child { border-bottom: none; }

.diff-field {
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
}

.diff-values {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.875rem;
  flex-wrap: wrap;
}
.diff-old { color: #9ca3af; }
.diff-old del { text-decoration: line-through; }
.diff-arrow { color: #9ca3af; font-size: 0.75rem; }
.diff-new { color: #047857; font-weight: 600; }

/* ── Footer ── */
.import-modal-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.75rem;
  background: white;
  border-top: 1px solid #e5e7eb;
}
.footer-spacer { flex: 1; }

.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  color: #374151;
  font-family: inherit;
}
.btn-action:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}
.btn-action:disabled { opacity: 0.55; cursor: not-allowed; }

.btn-action.btn-cancel { color: #6b7280; }

.btn-action.btn-link {
  border-color: transparent;
  background: transparent;
  color: #3b82f6;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.btn-action.btn-link:hover:not(:disabled) {
  background: #eff6ff;
  border-color: transparent;
}

.btn-action.btn-primary,
.btn-action.btn-create {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}
.btn-action.btn-primary:hover:not(:disabled),
.btn-action.btn-create:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-action.btn-create {
  background: #10b981;
  border-color: #10b981;
}
.btn-action.btn-create:hover:not(:disabled) {
  background: #059669;
  border-color: #059669;
}

.btn-action.btn-update {
  background: #f59e0b;
  border-color: #f59e0b;
  color: white;
}
.btn-action.btn-update:hover:not(:disabled) {
  background: #d97706;
  border-color: #d97706;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .import-modal-wrap { padding: 0; align-items: stretch; }
  .import-modal { border-radius: 0; max-width: 100%; min-height: 100vh; }
  .import-modal-header { padding: 1rem 1.25rem; }
  .import-icon { width: 44px; height: 44px; font-size: 1.2rem; }
  .import-title { font-size: 1.1rem; }
  .import-modal-body { padding: 1rem 1.25rem; max-height: none; }
  .import-modal-footer { padding: 0.875rem 1.25rem; flex-wrap: wrap; }
  .info-row, .diff-row { grid-template-columns: 1fr; gap: 0.2rem; }
  .btn-action span { display: inline; }
}
</style>
